package com.karyadina.ui.screens.flashcards.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.slideInVertically
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Notifications
import androidx.compose.material.icons.filled.Schedule
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material3.*
import androidx.compose.material3.ripple
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.karyadina.model.ExamType
import com.karyadina.model.FlashCard_Subject
import com.karyadina.model.SubjectStats
import com.karyadina.ui.theme.FlashcardColors
import com.karyadina.ui.theme.FlashcardIcons
import com.karyadina.ui.theme.ElevationProfile
import com.karyadina.ui.theme.ElevationUtils.rippleEffect
import kotlinx.coroutines.delay

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SubjectCard(
    subject: FlashCard_Subject,
    stats: SubjectStats,
    onClick: () -> Unit,
    onDelete: () -> Unit,
    itemIndex: Int = 0
) {
    var showDeleteDialog by remember { mutableStateOf(false) }
    var isVisible by remember { mutableStateOf(false) }

    // Staggered animation entry
    LaunchedEffect(itemIndex) {
        delay((itemIndex * 50L).coerceAtMost(300L))
        isVisible = true
    }

    AnimatedVisibility(
        visible = isVisible,
        enter = slideInVertically(
            initialOffsetY = { 30 }
        ) + fadeIn(),
    ) {
        ElevatedCard(
            onClick = onClick,
            modifier = Modifier
                .fillMaxWidth()
                .rippleEffect(bounded = true),
            elevation = CardDefaults.elevatedCardElevation(
                defaultElevation = ElevationProfile.raisedElevation
            )
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(16.dp)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        // Color indicator
                        Box(
                            modifier = Modifier
                                .size(48.dp)
                                .clip(CircleShape)
                                .background(com.karyadina.ui.theme.StudyTheme.parseHexColor(subject.color))
                        )

                        Column {
                            Text(
                                subject.name,
                                style = MaterialTheme.typography.titleMedium,
                                fontWeight = FontWeight.Bold
                            )
                            Text(
                                ExamType.valueOf(subject.examType).displayName,
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                            )
                        }
                    }

                    IconButton(onClick = { showDeleteDialog = true }) {
                        Icon(
                            Icons.Outlined.Delete,
                            "Delete",
                            tint = MaterialTheme.colorScheme.error
                        )
                    }
                }

                Spacer(modifier = Modifier.height(16.dp))

                // Progress Summary
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column {
                        Text(
                            "${stats.masteredCards} / ${stats.totalCards} Mastered",
                            style = MaterialTheme.typography.bodyMedium,
                            fontWeight = FontWeight.Bold
                        )
                        Text(
                            "${((stats.masteredCards.toFloat() / stats.totalCards.coerceAtLeast(1)) * 100).toInt()}% Complete",
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                        )
                    }

                    if (stats.dueForReview > 0) {
                        Surface(
                            shape = RoundedCornerShape(12.dp),
                            color = MaterialTheme.colorScheme.primaryContainer
                        ) {
                            Row(
                                modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
                                horizontalArrangement = Arrangement.spacedBy(4.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Icon(
                                    Icons.Filled.Notifications,
                                    contentDescription = null,
                                    modifier = Modifier.size(16.dp),
                                    tint = MaterialTheme.colorScheme.primary
                                )
                                Text(
                                    "${stats.dueForReview} due now",
                                    style = MaterialTheme.typography.labelMedium,
                                    color = MaterialTheme.colorScheme.primary,
                                    fontWeight = FontWeight.Bold
                                )
                            }
                        }
                    } else if (stats.totalCards > 0 && stats.masteredCards < stats.totalCards) {
                        Surface(
                            shape = RoundedCornerShape(12.dp),
                            color = MaterialTheme.colorScheme.surfaceVariant
                        ) {
                            Row(
                                modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
                                horizontalArrangement = Arrangement.spacedBy(4.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Icon(
                                    Icons.Filled.Schedule,
                                    contentDescription = null,
                                    modifier = Modifier.size(16.dp)
                                )
                                Text(
                                    "All reviewed",
                                    style = MaterialTheme.typography.labelMedium,
                                    fontWeight = FontWeight.Medium
                                )
                            }
                        }
                    }
                }

                Spacer(modifier = Modifier.height(8.dp))

                LinearProgressIndicator(
                    progress = { if (stats.totalCards > 0) stats.masteredCards.toFloat() / stats.totalCards else 0f },
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(8.dp)
                        .clip(RoundedCornerShape(4.dp)),
                    color = com.karyadina.ui.theme.StudyTheme.parseHexColor(subject.color),
                    trackColor = MaterialTheme.colorScheme.surfaceVariant
                )

                if (stats.totalCards > 0) {
                    Spacer(modifier = Modifier.height(12.dp))

                    // Mastery Level Distribution
                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Text(
                            "Mastery Breakdown",
                            style = MaterialTheme.typography.labelMedium,
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f)
                        )

                        // Note: MasteryChip is defined in StatsComponents.kt
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceEvenly
                        ) {
                            MasteryChip(
                                "New",
                                stats.newCards,
                                FlashcardColors.getMasteryColor("NEW")
                            )
                            MasteryChip(
                                "Learning",
                                stats.learningCards,
                                FlashcardColors.getMasteryColor("LEARNING")
                            )
                            MasteryChip(
                                "Review",
                                stats.reviewCards,
                                FlashcardColors.getMasteryColor("REVIEW")
                            )
                            MasteryChip(
                                "Mastered",
                                stats.masteredCards,
                                FlashcardColors.getMasteryColor("MASTERED")
                            )
                        }

                        // Progress explanation
                        if (stats.masteredCards == 0 && stats.newCards > 0) {
                            Text(
                                "💡 Tip: Review cards 5 times correctly to master them!",
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.primary,
                                modifier = Modifier.padding(top = 4.dp)
                            )
                        } else if (stats.masteredCards < stats.totalCards && stats.dueForReview == 0) {
                            Text(
                                "✅ Great! Come back later for the next review session.",
                                style = MaterialTheme.typography.bodySmall,
                                color = FlashcardColors.getCorrectAnswerColor(),
                                modifier = Modifier.padding(top = 4.dp)
                            )
                        }
                    }
                }
            }
        }  // End of AnimatedVisibility

        if (showDeleteDialog) {
            AlertDialog(
                onDismissRequest = { showDeleteDialog = false },
                title = { Text("Delete Subject?") },
                text = { Text("This will delete all units and flashcards in this subject. This action cannot be undone.") },
                confirmButton = {
                    TextButton(
                        onClick = {
                            onDelete()
                            showDeleteDialog = false
                        },
                        colors = ButtonDefaults.textButtonColors(
                            contentColor = MaterialTheme.colorScheme.error
                        )
                    ) {
                        Text("Delete")
                    }
                },
                dismissButton = {
                    TextButton(onClick = { showDeleteDialog = false }) {
                        Text("Cancel")
                    }
                }
            )
        }
    }
}
